import {
  boolean,
  date,
  index,
  integer,
  pgTable,
  real,
  text,
  timestamp,
  uniqueIndex,
} from "drizzle-orm/pg-core";
import { nanoid } from "nanoid";
import { user } from "./auth";
import { branches } from "./branches";
import { inventoryLots } from "./inventory";

export const PURCHASE_ORDER_STATUSES = [
  "DRAFT",
  "SUBMITTED",
  "APPROVED",
  "PARTIALLY_RECEIVED",
  "RECEIVED",
  "CLOSED",
  "CANCELLED",
] as const;

export type PurchaseOrderStatus = (typeof PURCHASE_ORDER_STATUSES)[number];

export const PO_LINE_ITEM_TYPES = ["INGREDIENT", "RETAIL_VARIANT"] as const;

export type PoLineItemType = (typeof PO_LINE_ITEM_TYPES)[number];

export const suppliers = pgTable(
  "suppliers",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    code: text("code").unique(),
    name: text("name").notNull(),
    contactName: text("contact_name"),
    phone: text("phone"),
    email: text("email"),
    address: text("address"),
    taxId: text("tax_id"),
    paymentTerms: text("payment_terms"),
    isActive: boolean("is_active").notNull().default(true),
    notes: text("notes"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [index("suppliers_active_idx").on(t.isActive)],
);

export const purchaseOrders = pgTable(
  "purchase_orders",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    poNo: text("po_no").notNull().unique(),
    supplierId: text("supplier_id")
      .notNull()
      .references(() => suppliers.id, { onDelete: "restrict" }),
    branchId: text("branch_id")
      .notNull()
      .references(() => branches.id, { onDelete: "restrict" }),
    status: text("status", {
      enum: PURCHASE_ORDER_STATUSES,
    }).notNull(),
    orderDate: date("order_date").notNull(),
    expectedDate: date("expected_date"),
    currency: text("currency").notNull().default("LAK"),
    subtotalLak: integer("subtotal_lak").notNull().default(0),
    note: text("note"),
    submittedAt: timestamp("submitted_at", {
      withTimezone: true,
      mode: "date",
    }),
    approvedAt: timestamp("approved_at", {
      withTimezone: true,
      mode: "date",
    }),
    approvedByUserId: text("approved_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    closedAt: timestamp("closed_at", {
      withTimezone: true,
      mode: "date",
    }),
    cancelledAt: timestamp("cancelled_at", {
      withTimezone: true,
      mode: "date",
    }),
    createdByUserId: text("created_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("purchase_orders_status_order_date_idx").on(t.status, t.orderDate),
    index("purchase_orders_supplier_idx").on(t.supplierId),
    index("purchase_orders_branch_status_idx").on(t.branchId, t.status),
  ],
);

export const purchaseOrderLines = pgTable(
  "purchase_order_lines",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    poId: text("po_id")
      .notNull()
      .references(() => purchaseOrders.id, { onDelete: "cascade" }),
    lineNo: integer("line_no").notNull(),
    itemType: text("item_type", { enum: PO_LINE_ITEM_TYPES }).notNull(),
    itemId: text("item_id").notNull(),
    unit: text("unit").notNull(),
    toBaseFactor: real("to_base_factor").notNull().default(1),
    qtyOrdered: real("qty_ordered").notNull(),
    qtyReceived: real("qty_received").notNull().default(0),
    unitCostLak: integer("unit_cost_lak").notNull(),
    note: text("note"),
  },
  (t) => [
    uniqueIndex("purchase_order_lines_po_id_line_no_unique").on(
      t.poId,
      t.lineNo,
    ),
    index("purchase_order_lines_po_idx").on(t.poId),
  ],
);

export const purchaseReceipts = pgTable(
  "purchase_receipts",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    receiptNo: text("receipt_no").notNull().unique(),
    poId: text("po_id")
      .notNull()
      .references(() => purchaseOrders.id, { onDelete: "restrict" }),
    branchId: text("branch_id")
      .notNull()
      .references(() => branches.id, { onDelete: "restrict" }),
    receivedAt: timestamp("received_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    note: text("note"),
    createdByUserId: text("created_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [index("purchase_receipts_po_idx").on(t.poId)],
);

export const purchaseReceiptLines = pgTable(
  "purchase_receipt_lines",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    receiptId: text("receipt_id")
      .notNull()
      .references(() => purchaseReceipts.id, { onDelete: "cascade" }),
    poLineId: text("po_line_id")
      .notNull()
      .references(() => purchaseOrderLines.id, { onDelete: "restrict" }),
    qtyReceived: real("qty_received").notNull(),
    lotId: text("lot_id")
      .notNull()
      .references(() => inventoryLots.id, { onDelete: "restrict" }),
    lotNo: text("lot_no").notNull(),
    expiryDate: date("expiry_date"),
  },
  (t) => [
    index("purchase_receipt_lines_receipt_idx").on(t.receiptId),
    index("purchase_receipt_lines_po_line_idx").on(t.poLineId),
  ],
);
